import { Link } from 'react-router-dom';
import { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-4 rounded-lg bg-background-50 hover:bg-background-100 transition-colors duration-200"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.imageAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1">
            {article.publishDate} · {article.readTime}
          </p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-100 transition-colors duration-200"
      >
        <div className="aspect-[16/9] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.imageAlt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <span className="inline-block text-xs font-medium text-primary-600 bg-primary-100/70 px-2.5 py-1 rounded-full mb-3">
            {article.tags[0]}
          </span>
          <h3 className="text-lg font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed mb-3">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.author}</span>
            <span>·</span>
            <span>{article.publishDate}</span>
            <span>·</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col sm:flex-row gap-5 p-5 rounded-lg bg-background-50 hover:bg-background-100 transition-colors duration-200"
    >
      <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-md overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-primary-600 bg-primary-100/70 px-2 py-0.5 rounded-full">
              {article.tags[0]}
            </span>
            <span className="text-xs text-foreground-500">{article.readTime}</span>
          </div>
          <h3 className="text-base font-bold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
            {article.excerpt}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-foreground-500 mt-3">
          <span>{article.author}</span>
          <span>·</span>
          <span>{article.publishDate}</span>
        </div>
      </div>
    </Link>
  );
}